import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Space, Modal, Select, Button, Table, Checkbox, Input } from  'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, PlusOutlined, RedoOutlined, SyncOutlined } from '@ant-design/icons'
import useHasError from '../../hooks/useHasError';
import useDataWarehouseCreateImport from '../../gql/hooks/useDataWarehouseCreateImport';
import './styles.less';
import { Layout } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { Card } from 'antd';
import { Routes } from '../../../routes';
import DATA_WAREHOUSE_IMPORTS from '../../gql/queries/DATA_WAREHOUSE_IMPORTS';
import { useLazyQuery } from '@apollo/client';
import { Tag } from 'antd';
import moment from 'moment';
import { Form } from 'antd';

function titleCase(text) {
    return text.toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.substring(1)).join(' ')
}

const importTypeChoices = [ { value: 'FULL', label: 'Full'}, {value: 'INCREMENTAL', label: 'Incremental'}];

const docTypeChoices = [
    'BRANDS',
    'BUNCHBALL_POINT_CATEGORIES',
    'CATEGORIES',
    'COMPANY_LOCALE_CODES',
    'COURSE_CERTIFICATES',
    'COURSES',
    'CURRICULUMS',
    'ENROLLMENTS',
    'LEARNING_OBJECTS',
    'LOGIN_RECORDS',
    'BUNCHBALL_LOGIN_RECORDS',
    'NOTIFICATIONS',
    'RESOURCES',
    'SEGMENTS',
    'TAGS',
    'USERS',
    'USER_ATTRIBUTE_TYPES'
].map( d => ({ value: d, label: titleCase(d) }) );

const CreateImportModal = ({ createImportCb }) => {

    const [form] = Form.useForm();

    const { createImport } = useDataWarehouseCreateImport();
    const [ showModal, setShowModal ] = useState( false );
    const [ isSaving, setIsSaving ] = useState( false );

    const importType = Form.useWatch('importType', form);
    const docTypes = Form.useWatch('docTypes', form);
    const requireConfirm = importType === 'FULL' && ( !docTypes || docTypes.length === 0 );

    async function handleCreateImport(values) {

        setIsSaving(true)

        const input = { importType: values.importType };

        if ( values.docTypes?.length > 0 ) {
            input.docTypes = values.docTypes;
        }

        const response = await createImport( input )

        setIsSaving(false)

        if(response.success) {
            createImportCb()
            setShowModal(false)
        } else {
            console.error( 'Error creating import' );
        }

    }

    const handleCreateClick = async () => {

        form.validateFields()
            .then(handleCreateImport)
            .catch(info => console.log('failed:', info))
    };

    const handleCancelClick = () => {
        setShowModal(false)
    };

    const handleOpen = () => {
        setShowModal(true)
        form.resetFields();
    }

    return <>
        <Button
            icon={ <PlusOutlined/> }
            onClick={ () => handleOpen() }
        >
            Create Import
        </Button>
        <Modal
            centered
            width={ 800 }
            className='create-import-modal'
            title="Create Import"
            open={ showModal }
            onOk={ () => form.submit() }
            onCancel={ handleCancelClick }
            confirmLoading={ isSaving }
            okText={ 'Create' }
        >
            <p>Select the type of import you want to create and click save. </p>

            <Form
                name='create-import'
                layout='vertical'
                form={form}
                initialValues={{ importType: undefined, docTypes: []}}
                onFinish={handleCreateClick}
                >

                <div className='selectors'>
                    <Form.Item
                        form={form}
                        label='Import Type'
                        name='importType'
                        htmlFor='Import Type'
                        rules={[{required: true, message: 'Import type is required'}]}
                        >

                        <Select
                            showSearch
                            placeholder='Select type...'
                            options={ importTypeChoices }
                        />
                    </Form.Item>
                    <Form.Item
                        label='Doc Types'
                        name='docTypes'
                        className='doc-type-checkboxes'
                        extra='Each document type you select runs as its own import job, so a single create can fan out into several jobs at once. Leave everything unselected to import all document types.'
                        >
                        <Checkbox.Group options={ docTypeChoices } />
                    </Form.Item>

                    { requireConfirm &&
                        <div className='confirm-full'>
                            <p>You've selected a <strong>FULL</strong> import with no specific doc types, which re-imports every document type. This is a resource-intensive operation that can take hours to complete. To confirm you'd like to proceed, please type "CONFIRM" into the field below.</p>
                            <Form.Item
                                label='Confirm Full Import'
                                name='confirmFull'
                                htmlFor='Confirm Full Import'
                                rules={[
                                    { required: true, message: 'Please type CONFIRM to proceed' },
                                    { validator: ( _, value ) => value === 'CONFIRM' ? Promise.resolve() : Promise.reject(new Error('You must type CONFIRM exactly to create a FULL import')) }
                                ]}
                                >
                                <Input placeholder='Type CONFIRM' />
                            </Form.Item>
                        </div>
                    }
                </div>

            </Form>
        </Modal>
    </>
}

const WarehouseImportsPage = ( ) => {
    const [ fetchImportsList, { data, loading, error } ] = useLazyQuery( DATA_WAREHOUSE_IMPORTS );
    const { hasError } = useHasError( { items: [ error ] } );

    const [prevSearchVars, setPrevSearchVars] = useState();
    const [refreshing, setRefreshing] = useState(false);
    const disabled = useMemo(() => loading || refreshing, [loading, refreshing])

    const [selectedImportTypes, setSelectedImportTypes] = useState([])
    const [selectedDocTypes, setSelectedDocTypes] = useState([])
    const [selectedStatuses, setSelectedStatuses] = useState([])
    const [selectedDir, setSelectedDir] = useState('desc')
    const [selectedSort, setSelectedSort] = useState('createdAt')
    const [currentPage, setCurrentPage] = useState(1);

    const statusChoices = [ { value: 'COMPLETED', label: 'Completed'}, {value: 'ERROR', label: 'Error'}, {value: 'IN_PROGRESS', label: 'In Progress'}, {value: 'PENDING', label: 'Pending'}];

    const isNewFilters = useMemo(() => {
        if(!prevSearchVars) return true;
        const currVars = getUpdatedVars();

        const isEqual = (a, b) => {
            return a == b || (Array.isArray(a) && Array.isArray(b) && a.length == b.length && a.every(x => b.includes(x)))
        }

        return !(isEqual(currVars.importTypes, prevSearchVars.importTypes) &&
            isEqual(currVars.docTypes, prevSearchVars.docTypes) &&
            isEqual(currVars.statuses, prevSearchVars.statuses));

    }, [prevSearchVars, selectedImportTypes, selectedDocTypes, selectedStatuses])

    function getUpdatedVars(newVars = null) {
        return {
            importTypes: newVars?.importTypes ?? selectedImportTypes,
            docTypes: newVars?.docTypes ?? selectedDocTypes,
            statuses: newVars?.statuses ?? selectedStatuses,
            page: newVars?.page ?? currentPage,
            limit: 10,
            orderBy: selectedSort,
            orderDir: newVars?.orderDir ?? selectedDir
        }
    }

    function search(newVars) {

        const updatedVars = getUpdatedVars(newVars)

        setPrevSearchVars(updatedVars);

        fetchImportsList({
            variables: {options: updatedVars},
            fetchPolicy: 'network-only'
        })

        setRefreshing(false)
    }

    const currentData = useMemo(() => {
        if(!data) return [];
        const tableData = data?.dataWarehouseImports?.edges?.map(e => e?.node) ?? [];
        setRefreshing(false);
        return tableData;
    }, [data])

    useEffect( () => {
        search()
    }, [] );

    const handleTableChange = ( pagination, filters, sorter ) => {
        const order =  sorter.order == 'ascend' ? 'asc' : 'desc';
        setCurrentPage( pagination.current );
        setSelectedDir(order);
        setSelectedSort( sorter.column?.dataIndex );

        search({...prevSearchVars, page: pagination.current, orderDir: order, orderBy: sorter.column?.dataIndex})
    };

    const applyFilters = () => {
        setCurrentPage(1)
        search({ page: 1 })
    };

    const columns =  [
        {
            key: 'importType',
            dataIndex: 'importType',
            title: 'Type',
            className: 'name-column',
            fixed: 'left',
            render: ( importType ) => importType ? titleCase( importType ) : ''
        },
        {
            key: 'docType',
            dataIndex: 'docType',
            title: 'Doc Type',
            render: ( docType ) => docType ? titleCase( docType ) : ''
        },
        {
            key: 'status',
            dataIndex: 'status',
            title: 'Status',
            className: 'status-column',
            render: ( status ) => status == 'COMPLETED' ? <Tag color='green'><CheckCircleOutlined /> {titleCase(status)}</Tag> :
                status == 'IN_PROGRESS' ? <Tag color='blue'><SyncOutlined spin/> {titleCase(status)}</Tag> :
                status == 'ERROR' ? <Tag color='red'><CloseCircleOutlined /> {titleCase(status)}</Tag> :
                <Tag color='grey'><ClockCircleOutlined /> {titleCase(status)}</Tag>
        },
        {
            key: 'startedAt',
            dataIndex: 'startedAt',
            title: 'Started At',
            render: (time) => time ? moment(time).format('lll') : ''
        },
        {
            key: 'completedAt',
            dataIndex: 'completedAt',
            title: 'Completed At',
            render: (time) => time ? moment(time).format('lll') : ''
        },
        {
            key: 'createdAt',
            dataIndex: 'createdAt',
            title: 'Created At',
            sortKey: 'createdAt',
            defaultSortOrder: 'descend',
            sortDirections: ['descend','ascend'],
            render: (time) => time ? moment(time).format('lll') : '',
            sorter: () => 0,
        },
        {
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            title: 'Updated At',
            render: (time) => time ? moment(time).format('lll') : ''
        }
    ];

    function onImportCreation() {
        applyDefaultSearch();
    }

    function applyDefaultSearch() {
        setCurrentPage(1);
        setSelectedSort('createdAt')
        setSelectedDir('desc')
        setSelectedImportTypes([]);
        setSelectedDocTypes([]);
        setSelectedStatuses([]);
        search({ page: 1, orderDir: 'desc', orderBy: 'createdAt', importTypes: [], docTypes: [], statuses: [] })

    }

    function onRefresh() {

        setRefreshing(true);
        search(prevSearchVars);
    }

    const breadcrumbs = [
            { name: 'Course Studio', path: Routes.Home.buildPath() },
            { name: 'Analytics', path: Routes.Reporting.buildPath() },
            { name: 'Warehouse Imports'}
        ];

    return (
        <Layout className="warehouse-imports-page">

            { /* Page header breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            <Card
                className="warehouse-imports-content"
                title="Warehouse Imports"
                extra={<Button disabled={disabled} onClick={onRefresh} type='primary'>{refreshing ? <SyncOutlined spin /> : <RedoOutlined />} Refresh</Button>}
            >
                <div className="reports-list">

                    { /* Error Alert */ }
                    { hasError &&
                        <Alert
                            type="error"
                            message="Error"
                            description="An unknown error occurred. Please try again later."
                        />
                    }

                    { /* Imports list */ }
                    { !hasError &&
                        <>
                        <div className="toolbar">
                            <Space className='filters'>

                                <Select
                                    mode="multiple"
                                    allowClear
                                    disabled={disabled}
                                    placeholder="Filter by Type"
                                    value={ selectedImportTypes }
                                    onChange={ ( item ) => setSelectedImportTypes( item ) }
                                    options={ importTypeChoices }
                                />

                                <Select
                                    mode="multiple"
                                    allowClear
                                    disabled={disabled}
                                    placeholder="Filter by Doc Type"
                                    value={ selectedDocTypes }
                                    onChange={ ( item ) => setSelectedDocTypes( item ) }
                                    options={ docTypeChoices }
                                />

                                <Select
                                    mode="multiple"
                                    allowClear
                                    disabled={disabled}
                                    placeholder="Filter by Status"
                                    value={ selectedStatuses }
                                    onChange={ ( item ) => setSelectedStatuses( item ) }
                                    options={ statusChoices }
                                />

                                <Button
                                    type="primary"
                                    disabled={disabled || !isNewFilters}
                                    onClick={ applyFilters }
                                >
                                    Apply Filters
                                </Button>
                            </Space>

                            <Space className='create'>
                                <CreateImportModal createImportCb={onImportCreation} />

                            </Space>

                        </div>

                        <Alert description={<span><b>Important Notice:</b> Incremental imports are scheduled to run hourly, on the hour. Any import jobs created here will run as soon as possible, however, only one import job runs at a time, and so there may be a delay before a requested import job begins.</span>} type='warning' />

                        <Table
                            rowKey='id'
                            loading= { loading }
                            columns={ columns }
                            dataSource={ currentData }
                            onChange={ handleTableChange }
                            pagination={ {
                                total: data?.dataWarehouseImports?.pagination?.totalCount,
                                current: currentPage,
                                showQuickJumper: false,
                                showSizeChanger: false
                            } }
                        />
                        </>
                    }
                </div>
            </Card>
        </Layout>
    );
};

export default WarehouseImportsPage;
